import { JsonSchemaAnyOf, JsonSchemaArray, JsonSchemaBoolean, JsonSchemaConst, JsonSchemaCustomType, JsonSchemaEnum, JsonSchemaNull, JsonSchemaNumber, JsonSchemaObject, JsonSchemaOneOf, JsonSchemaRef, JsonSchemaString, } from "./json-schema-model.js";
import { StringJsonWriter } from "./string-json-writer.js";
import { assertNot } from "@dataspecer/core/core";
export async function writeJsonSchema(schema, stream) {
    const writer = StringJsonWriter.createObject(stream);
    await writer.valueIfNotNull("$schema", schema.schema);
    await writer.valueIfNotNull("$id", schema.id);
    await writeJsonDefinition(writer, schema.root);
    await writer.closeObject();
}
async function writeJsonDefinition(writer, schema) {
    if (JsonSchemaRef.is(schema)) {
        return writeJsonSchemaRef(writer, schema);
    }
    await writeJsonSchemaDefinitionProperties(writer, schema);
    if (JsonSchemaObject.is(schema)) {
        return writeJsonSchemaObject(writer, schema);
    }
    else if (JsonSchemaArray.is(schema)) {
        return writeJsonSchemaArray(writer, schema);
    }
    else if (JsonSchemaNull.is(schema)) {
        return writeJsonSchemaNull(writer);
    }
    else if (JsonSchemaBoolean.is(schema)) {
        return writeJsonSchemaBoolean(writer);
    }
    else if (JsonSchemaNumber.is(schema)) {
        return writeJsonSchemaNumber(writer, schema.isInteger);
    }
    else if (JsonSchemaString.is(schema)) {
        return writeJsonSchemaString(writer, schema);
    }
    else if (JsonSchemaAnyOf.is(schema)) {
        return writeJsonSchemaAnyOf(writer, schema);
    }
    else if (JsonSchemaOneOf.is(schema)) {
        return writeJsonSchemaOneOf(writer, schema);
    }
    else if (JsonSchemaConst.is(schema)) {
        return writeJsonSchemaConst(writer, schema);
    }
    else if (JsonSchemaEnum.is(schema)) {
        return writeJsonSchemaEnum(writer, schema);
    }
    else if (JsonSchemaCustomType.is(schema)) {
        return writeJsonSchemaCustomType(writer, schema);
    }
}
async function writeJsonSchemaDefinitionProperties(writer, schema) {
    await writer.valueIfNotNull("title", schema.title);
    await writer.valueIfNotNull("description", schema.description);
}
async function writeJsonSchemaObject(writer, schema) {
    await writer.value("type", "object");
    if (schema.additionalProperties === false) {
        await writer.value("additionalProperties", false);
    }
    else if (schema.additionalProperties !== null) {
        const propWriter = writer.object("additionalProperties");
        await writeJsonDefinition(propWriter, schema.additionalProperties);
        await propWriter.closeObject();
    }
    const required = writer.array("required");
    for (const key of schema.required) {
        await required.value(key);
    }
    await required.closeArray();
    if (schema.objectExamples.length > 0) {
        const examples = writer.array("examples");
        for (const example of schema.objectExamples) {
            await examples.value(example);
        }
        await examples.closeArray();
    }
    const properties = writer.object("properties");
    for (const [key, value] of Object.entries(schema.properties)) {
        const property = properties.object(key);
        await writeJsonDefinition(property, value);
        await properties.closeObject();
    }
    await properties.closeObject();
}
async function writeJsonSchemaArray(writer, schema) {
    await writer.value("type", "array");
    if (schema.minItems !== null) {
        await writer.value("minItems", schema.minItems);
    }
    if (schema.maxItems !== null) {
        await writer.value("maxItems", schema.maxItems);
    }
    const items = writer.object("items");
    assertNot(schema.items === null, "Missing items specification.");
    await writeJsonDefinition(items, schema.items);
    await items.closeObject();
}
async function writeJsonSchemaNull(writer) {
    await writer.value("type", "null");
}
async function writeJsonSchemaBoolean(writer) {
    await writer.value("type", "boolean");
}
async function writeJsonSchemaNumber(writer, isInteger) {
    await writer.value("type", isInteger ? "integer" : "number");
}
async function writeJsonSchemaString(writer, schema) {
    await writer.value("type", "string");
    await writer.valueIfNotNull("format", schema.format);
    await writer.valueIfNotNull("pattern", schema.pattern);
    if (schema.examples && schema.examples.length > 0) {
        const array = writer.array("examples");
        for (const example of schema.examples) {
            await array.value(example);
        }
        await array.closeArray();
    }
}
async function writeJsonSchemaAnyOf(writer, schema) {
    const array = writer.array("anyOf");
    for (const definition of schema.types) {
        const valueWriter = array.object();
        await writeJsonDefinition(writer, definition);
        await valueWriter.closeObject();
    }
    await array.closeArray();
}
async function writeJsonSchemaOneOf(writer, schema) {
    const array = writer.array("oneOf");
    for (const definition of schema.types) {
        const valueWriter = array.object();
        await writeJsonDefinition(writer, definition);
        await valueWriter.closeObject();
    }
    await array.closeArray();
}
async function writeJsonSchemaConst(writer, schema) {
    await writer.value("const", schema.value);
}
async function writeJsonSchemaEnum(writer, schema) {
    const array = writer.array("enum");
    for (const value of schema.values) {
        await array.value(value);
    }
    await array.closeArray();
}
async function writeJsonSchemaRef(writer, schema) {
    await writer.value("$ref", schema.url);
}
async function writeJsonSchemaCustomType(writer, schema) {
    await objectToJsonWriter(schema.data, writer);
}
async function objectToJsonWriter(o, writer) {
    for (const [key, value] of Object.entries(o)) {
        // value is array
        if (Array.isArray(value)) {
            const array = writer.array(key);
            await arrayToJsonWriter(value, array);
            await array.closeArray();
        }
        else if (typeof value === "object") {
            const object = writer.object(key);
            await objectToJsonWriter(value, object);
            await object.closeObject();
        }
        else {
            await writer.value(key, value);
        }
    }
}
async function arrayToJsonWriter(a, writer) {
    for (const item of a) {
        if (Array.isArray(item)) {
            const array = writer.array();
            await arrayToJsonWriter(item, array);
            await array.closeArray();
        }
        else if (typeof item === "object") {
            const object = writer.object();
            await objectToJsonWriter(item, object);
            await object.closeObject();
        }
        else {
            await writer.value(item);
        }
    }
}
//# sourceMappingURL=json-schema-writer.js.map